import dagre from "@dagrejs/dagre";
import Badge from "@intility/bifrost-react/Badge";
import {
  Background,
  type Edge,
  Handle,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
  type ReactFlowProps,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  BlueprintNode,
  BlueprintResponse,
  PlatformName,
} from "~/api/fga-types";
import "./BlueprintCanvas.css";

export type BlueprintNodeKind = "company" | "platform" | "resource";

export type BlueprintSelection =
  | { kind: "company" }
  | { kind: "platform"; platformSlug: string; displayName: string }
  | {
      kind: "resource";
      platformSlug: string;
      usageId: string;
      typeId: string;
      typeName: string;
      isStatic: boolean;
      nameRegex: string | null;
      qualifiedName: string | null;
    };

type NodeData = {
  label: string;
  kind: BlueprintNodeKind;
  typeKind?: "static" | "dynamic";
  staged?: boolean;
  pendingDelete?: boolean;
  selected?: boolean;
  pulse?: boolean;
  canAddChild?: boolean;
  hasChildren: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
  onAddChild: () => void;
};

const NODE_WIDTH = 240;
const NODE_HEIGHT = 56;

function NodeCard({ data }: NodeProps<Node<NodeData>>) {
  const className = [
    "bp-canvas-node",
    `bp-canvas-node-${data.kind}`,
    data.pendingDelete ? "bp-canvas-node-pending-delete" : "",
    data.selected ? "bp-canvas-node-selected" : "",
    data.pulse ? "bp-canvas-node-pulse" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleToggle = (e: ReactMouseEvent) => {
    e.stopPropagation();
    data.onToggle();
  };

  const handleAdd = (e: ReactMouseEvent) => {
    e.stopPropagation();
    data.onAddChild();
  };

  return (
    <div className={className} title={data.label}>
      <Handle type="target" position={Position.Top} isConnectable={false} />
      <div className="bp-canvas-node-content">
        <span className="bp-canvas-node-label">{data.label}</span>
        {data.pendingDelete && (
          <Badge state="alert" pill>
            deleted
          </Badge>
        )}
        {data.staged && !data.pendingDelete && (
          <Badge state="success" pill>
            new
          </Badge>
        )}
        {data.typeKind && !data.pendingDelete && (
          <Badge state={data.typeKind === "static" ? "neutral" : "chill"} pill>
            {data.typeKind === "static" ? "Static" : "Dynamic"}
          </Badge>
        )}
      </div>
      {data.hasChildren && (
        <button
          type="button"
          className="bp-canvas-node-toggle"
          onClick={handleToggle}
          aria-label={data.isCollapsed ? "Expand" : "Collapse"}
          title={data.isCollapsed ? "Expand" : "Collapse"}
        >
          {data.isCollapsed ? "▸" : "▾"}
        </button>
      )}
      {data.canAddChild && (
        <button
          type="button"
          className="bp-canvas-node-add"
          onClick={handleAdd}
          aria-label="Add child resource"
          title="Add child resource"
        >
          +
        </button>
      )}
      <Handle type="source" position={Position.Bottom} isConnectable={false} />
    </div>
  );
}

const nodeTypes = { card: NodeCard };

function buildGraph(
  blueprint: BlueprintResponse,
  platforms: PlatformName[],
  selectedId: string | null,
  collapsed: Set<string>,
  singlePlatform?: string | null,
  pulseId?: string | null,
  enableAddButton?: boolean,
  stagedNodeIds?: Set<string>,
  deletedNodeIds?: Set<string>,
): {
  nodes: Node<NodeData>[];
  edges: Edge[];
  selectionById: Map<string, BlueprintSelection>;
} {
  const nodes: Node<NodeData>[] = [];
  const edges: Edge[] = [];
  const selectionById = new Map<string, BlueprintSelection>();

  const platformDisplayName = (platformSlug: string): string => {
    const parts = platformSlug.split("/");
    const localSlug = parts[parts.length - 1] ?? platformSlug;
    return (
      platforms.find((p) => p.slug === localSlug)?.displayName ?? localSlug
    );
  };

  const pushResource = (
    platformSlug: string,
    node: BlueprintNode,
    parentId: string,
    isDeleted: boolean,
  ) => {
    const id = `resource:${node.usageId}`;
    const typeKind = node.type.isStatic ? "static" : "dynamic";
    const isStaged = stagedNodeIds?.has(node.usageId) ?? false;
    nodes.push({
      id,
      type: "card",
      position: { x: 0, y: 0 },
      data: {
        label: node.type.displayName,
        kind: "resource",
        typeKind,
        staged: isStaged,
        pendingDelete: isDeleted,
        selected: selectedId === id,
        canAddChild: enableAddButton && !isDeleted,
        hasChildren: node.children.length > 0,
        isCollapsed: collapsed.has(id),
        onToggle: () => {},
        onAddChild: () => {},
      },
    });
    selectionById.set(id, {
      kind: "resource",
      platformSlug,
      usageId: node.usageId,
      typeId: node.type.id,
      typeName: node.type.displayName,
      isStatic: node.type.isStatic,
      nameRegex: node.type.nameRegex ?? null,
      qualifiedName: node.qualifiedName ?? null,
    });
    edges.push({
      id: `${parentId}->${id}`,
      source: parentId,
      target: id,
      type: "smoothstep",
    });
    if (!collapsed.has(id)) {
      for (const child of node.children) {
        pushResource(
          platformSlug,
          child,
          id,
          isDeleted || (deletedNodeIds?.has(child.usageId) ?? false),
        );
      }
    }
  };

  if (singlePlatform) {
    const sp = blueprint.platforms.find((p) => p.platform === singlePlatform);
    if (sp) {
      const platformId = `platform:${sp.platform}`;
      nodes.push({
        id: platformId,
        type: "card",
        position: { x: 0, y: 0 },
        data: {
          label: platformDisplayName(sp.platform),
          kind: "platform",
          selected: selectedId === platformId,
          pulse: pulseId === platformId,
          canAddChild: enableAddButton,
          hasChildren: sp.tree.length > 0,
          isCollapsed: collapsed.has(platformId),
          onToggle: () => {},
          onAddChild: () => {},
        },
      });
      selectionById.set(platformId, {
        kind: "platform",
        platformSlug: sp.platform,
        displayName: platformDisplayName(sp.platform),
      });
      if (!collapsed.has(platformId)) {
        for (const root of sp.tree) {
          pushResource(
            sp.platform,
            root,
            platformId,
            deletedNodeIds?.has(root.usageId) ?? false,
          );
        }
      }
    }
  } else {
    // The root card groups every blueprint. Blueprints have no company (ADR-0056),
    // so this is a fixed graph id, not an object id.
    const companyId = "company:blueprints";
    nodes.push({
      id: companyId,
      type: "card",
      position: { x: 0, y: 0 },
      data: {
        label: "Platforms",
        kind: "company",
        selected: selectedId === companyId,
        hasChildren: blueprint.platforms.length > 0,
        isCollapsed: collapsed.has(companyId),
        onToggle: () => {},
        onAddChild: () => {},
      },
    });
    selectionById.set(companyId, { kind: "company" });

    if (collapsed.has(companyId)) {
      return { nodes, edges, selectionById };
    }

    for (const platform of blueprint.platforms) {
      const platformId = `platform:${platform.platform}`;
      nodes.push({
        id: platformId,
        type: "card",
        position: { x: 0, y: 0 },
        data: {
          label: platformDisplayName(platform.platform),
          kind: "platform",
          selected: selectedId === platformId,
          pulse: pulseId === platformId,
          canAddChild: enableAddButton,
          hasChildren: platform.tree.length > 0,
          isCollapsed: collapsed.has(platformId),
          onToggle: () => {},
          onAddChild: () => {},
        },
      });
      selectionById.set(platformId, {
        kind: "platform",
        platformSlug: platform.platform,
        displayName: platformDisplayName(platform.platform),
      });
      edges.push({
        id: `${companyId}->${platformId}`,
        source: companyId,
        target: platformId,
        type: "smoothstep",
      });
      if (!collapsed.has(platformId)) {
        for (const root of platform.tree) {
          pushResource(
            platform.platform,
            root,
            platformId,
            deletedNodeIds?.has(root.usageId) ?? false,
          );
        }
      }
    }
  }

  return { nodes, edges, selectionById };
}

function layoutTopDown(
  nodes: Node<NodeData>[],
  edges: Edge[],
): Node<NodeData>[] {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 32, ranksep: 64 });
  for (const node of nodes) {
    g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }
  dagre.layout(g);
  return nodes.map((node) => {
    const { x, y } = g.node(node.id);
    return {
      ...node,
      position: { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 },
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
    };
  });
}

export interface BlueprintCanvasProps {
  blueprint: BlueprintResponse;
  platforms: PlatformName[];
  selectedId: string | null;
  onSelect: (id: string | null, selection: BlueprintSelection | null) => void;
  singlePlatform?: string | null;
  pulseId?: string | null;
  onAddChild?: (id: string, selection: BlueprintSelection | null) => void;
  enableAddButton?: boolean;
  stagedNodeIds?: Set<string>;
  deletedNodeIds?: Set<string>;
}

function BlueprintCanvasInner({
  blueprint,
  platforms,
  selectedId,
  onSelect,
  singlePlatform,
  pulseId,
  onAddChild,
  enableAddButton,
  stagedNodeIds,
  deletedNodeIds,
}: BlueprintCanvasProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () =>
      new Set(
        singlePlatform
          ? []
          : blueprint.platforms.map((p) => `platform:${p.platform}`),
      ),
  );

  const toggleNode = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const {
    nodes: rawNodes,
    edges: graphEdges,
    selectionById,
  } = useMemo(
    () =>
      buildGraph(
        blueprint,
        platforms,
        selectedId,
        collapsed,
        singlePlatform,
        pulseId,
        enableAddButton,
        stagedNodeIds,
        deletedNodeIds,
      ),
    [
      blueprint,
      platforms,
      selectedId,
      collapsed,
      singlePlatform,
      pulseId,
      enableAddButton,
      stagedNodeIds,
      deletedNodeIds,
    ],
  );

  const graphNodes = useMemo(
    () =>
      rawNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          onToggle: () => toggleNode(node.id),
          onAddChild: () =>
            onAddChild?.(node.id, selectionById.get(node.id) ?? null),
        },
      })),
    [rawNodes, toggleNode, onAddChild, selectionById],
  );

  const laidOut = useMemo(
    () => layoutTopDown(graphNodes, graphEdges),
    [graphNodes, graphEdges],
  );

  const [nodes, setNodes, onNodesChange] =
    useNodesState<Node<NodeData>>(laidOut);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(graphEdges);
  const { fitView } = useReactFlow();
  const prevBlueprintRef = useRef<BlueprintResponse | null>(null);

  useEffect(() => {
    setNodes(laidOut);
    setEdges(graphEdges);
    // Only refit when the blueprint data itself changes, not on collapse toggles.
    if (prevBlueprintRef.current !== blueprint) {
      prevBlueprintRef.current = blueprint;
      requestAnimationFrame(() => fitView({ padding: 0.2, duration: 300 }));
    }
  }, [laidOut, graphEdges, setNodes, setEdges, fitView, blueprint]);

  const handleNodeClick: ReactFlowProps["onNodeClick"] = (_, node) => {
    const sel = selectionById.get(node.id) ?? null;
    onSelect(node.id, sel);
  };

  const handlePaneClick: ReactFlowProps["onPaneClick"] = () => {
    onSelect(null, null);
  };

  return (
    <div className="bp-canvas-wrapper">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        proOptions={{ hideAttribution: true }}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.2}
        maxZoom={1.5}
      >
        <Background gap={20} />
      </ReactFlow>
    </div>
  );
}

export default function BlueprintCanvas(props: BlueprintCanvasProps) {
  return (
    <ReactFlowProvider>
      <BlueprintCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
